import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';
import {isEmptyArray} from 'ramda-adjunct';

import {getApiErrorMessages} from '@dms/api/utils';
import {
  useGetMetadaServiceCaseQuery,
  useGetVehicleServiceChecklistsQuery,
  usePostVehicleServiceChecklistMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

const MazdaChecklistChooseFormSchema = z.object({
  checklistDefinitionId: z.string(),
});

type MazdaChecklistChooseFormType = z.infer<typeof MazdaChecklistChooseFormSchema>;

interface MazdaChecklistChooseDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
}

export function MazdaChecklistChooseDialog(props: MazdaChecklistChooseDialogProps) {
  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: isServiceCaseError,
    error: serviceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});

  const vehicleId = serviceCase?.vehicleId;

  const {
    data: checklists,
    isFetching: isChecklistsFetching,
    isError: isChecklistsError,
    error: checklistsError,
  } = useGetVehicleServiceChecklistsQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    // Mazda stops offering a definition once its checklist is open, so cached options go stale.
    refetchOnMountOrArgChange: true,
  });

  const [postVehicleServiceChecklist, {isLoading: isCreating}] =
    usePostVehicleServiceChecklistMutation();

  // Mazda rejects the live call with an actionable message (e.g. portal login required), so the
  // generic error status would hide the only hint the user can act on.
  const apiError = checklistsError ?? serviceCaseError;
  const apiErrorMessages = isNotNil(apiError) ? getApiErrorMessages(apiError) : undefined;
  const errorMessage = Array.isArray(apiErrorMessages)
    ? apiErrorMessages.join(' ')
    : apiErrorMessages;

  const checklistOptions = (checklists?.checklistDefinition ?? []).flatMap((definition) => {
    // Mazda puts the human-readable text in description and the internal code in name; both are
    // nullable and come back blank often enough that an empty one has to fall through, not render.
    const label = definition.description?.trim() || definition.name?.trim() || undefined;

    return isNotNil(definition.id) && isNotNil(label)
      ? [{value: definition.id.toString(), label}]
      : [];
  });

  const handleSubmit: FormSubmitHandler<MazdaChecklistChooseFormType> = (data) => {
    if (isNil(vehicleId)) {
      return Promise.resolve();
    }

    return postVehicleServiceChecklist({
      vehicleId,
      serviceCaseId: props.serviceCaseId,
      body: {
        serviceOrderId: props.serviceOrderId,
        checklistDefinitionId: Number(data.checklistDefinitionId),
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  return (
    <DataStatus
      isLoading={isServiceCaseLoading || isChecklistsFetching}
      isError={isServiceCaseError || isChecklistsError}
      errorMessage={errorMessage}
      isEmpty={isEmptyArray(checklistOptions)}
      emptyMessage={i18n.t('entity.orderIntegration.labels.mazdaChecklistEmpty')}
      emptySubheadline={i18n.t('entity.orderIntegration.labels.mazdaChecklistEmptyDescription')}
      minHeight={50}
    >
      <Form<MazdaChecklistChooseFormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={MazdaChecklistChooseFormSchema}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="checklistDefinitionId"
              type="choice"
              label={i18n.t('entity.checklist.labels.title')}
              options={checklistOptions}
              // The dialog body clips the dropdown, so the menu has to render in a portal.
              menuInPortal
              isNotClearable
              isRequired
              data-testid={suffixTestId('checklistDefinitionId', props)}
            />
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  isDisabled={isCreating}
                  data-testid={suffixTestId('discard', props)}
                />
                <FormButton
                  control={control}
                  title={i18n.t('general.actions.confirm')}
                  type="submit"
                  isLoading={isCreating}
                  data-testid={suffixTestId('confirm', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
